import { hasLinks, hasNextEvent, hasPersonPortraitPanel } from './entityHubRenderable.js';
import { getMatchKind, isOfficialChannelEvent } from './eventMatchKind.js';
import { safeMediaUrl } from './mediaUrl.js';
import { formatRelativeDayLabel } from './relativeDay.js';

const LIVE_PLATFORM_LABEL = {
  kick: 'Kick',
  twitch: 'Twitch',
  youtube: 'YouTube',
};

function formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const day = formatRelativeDayLabel(d, { month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${day} · ${time}`;
  } catch {
    return null;
  }
}

export function nextEventLabel(nextEvent) {
  if (!nextEvent) return null;
  return [
    nextEvent.title,
    nextEvent.venueName,
    formatWhen(nextEvent.startsAt),
  ].filter(Boolean).join(' · ') || null;
}

function sameMediaUrl(a, b) {
  const left = safeMediaUrl(a);
  const right = safeMediaUrl(b);
  if (!left || !right) return false;
  return left === right;
}

function venueKey(concert) {
  return String(concert?.venueName || '').trim().toLowerCase();
}

/** Same promo still on two different venues is tour art, not a stadium shot. */
export function looksLikeSharedTourArt(hub, url) {
  const src = safeMediaUrl(url);
  if (!src) return false;
  const concerts = Array.isArray(hub?.concerts) ? hub.concerts : [];
  const venues = new Set();
  for (const concert of concerts) {
    if (!sameMediaUrl(concert.venueImageUrl, src) && !sameMediaUrl(concert.imageUrl, src)) {
      continue;
    }
    const venue = venueKey(concert);
    if (venue) venues.add(venue);
  }
  return venues.size > 1;
}

function distinctVenueImage(hub, nextEvent) {
  const raw = nextEvent?.venueImageUrl;
  if (!raw) return null;
  const concerts = Array.isArray(hub?.concerts) ? hub.concerts : [];
  const match = concerts.find((concert) => concert.id && concert.id === nextEvent.id)
    || concerts[0]
    || null;
  if (match && sameMediaUrl(raw, match.imageUrl)) return null;
  if (looksLikeSharedTourArt(hub, raw)) return null;
  return safeMediaUrl(raw);
}

function compactName(value) {
  return String(value || '')
    .trim()
    .replace(/^@/, '')
    .replace(/\s+/g, '')
    .toLowerCase();
}

function livePlatform(event) {
  const platform = String(event?.platform || '').toLowerCase();
  return LIVE_PLATFORM_LABEL[platform] ? platform : null;
}

function stripUrl(url) {
  return String(url || '')
    .trim()
    .toLowerCase()
    .replace(/\/+$/, '');
}

function eventMatchesHubIdentity(event, hub) {
  const platform = livePlatform(event);
  if (!platform) return false;

  const login = compactName(event.channelLogin);
  const channelTitle = compactName(event.channelTitle);
  const channelId = compactName(event.channelId);
  const liveUrl = stripUrl(event.url);

  const chips = [
    ...(Array.isArray(hub?.streamingPlatforms) ? hub.streamingPlatforms : []),
    ...(Array.isArray(hub?.links) ? hub.links : []),
  ];
  for (const chip of chips) {
    const key = String(chip?.key || chip?.kind || '').toLowerCase();
    if (key !== platform) continue;
    const hubUrl = stripUrl(chip.url);
    if (hubUrl && liveUrl && (liveUrl === hubUrl || liveUrl.startsWith(`${hubUrl}/`) || hubUrl.startsWith(liveUrl))) {
      return true;
    }
    if (login && hubUrl.includes(`/${login}`)) return true;
  }

  const ids = hub?.entity?.platformIds;
  if (ids && typeof ids === 'object') {
    const id = compactName(ids[platform]);
    if (id && (id === login || id === channelTitle || id === channelId)) {
      return true;
    }
  }

  const entityName = compactName(hub?.entity?.name);
  if (entityName && (entityName === login || entityName === channelTitle)) {
    return true;
  }

  return false;
}

function liveHeroScore(event) {
  const official = isOfficialChannelEvent(event) ? 1_000_000 : 0;
  const viewers = Number(event?.viewers);
  return official + (Number.isFinite(viewers) ? viewers : 0);
}

/**
 * Official or identity-matched live YouTube / Twitch / Kick row from gallery results.
 * Random category streams must not become the person hero.
 */
export function pickLiveHeroEvent(results, hub) {
  const candidates = (results || []).filter((event) => {
    if (!livePlatform(event)) return false;
    if (getMatchKind(event) === 'related') return false;
    return isOfficialChannelEvent(event) || eventMatchesHubIdentity(event, hub);
  });
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => liveHeroScore(b) - liveHeroScore(a))[0];
}

export function streamingLinksFromLiveEvent(event) {
  const platform = livePlatform(event);
  if (!platform || !event?.url) return [];
  return [{
    label: LIVE_PLATFORM_LABEL[platform],
    url: event.url,
    kind: platform,
  }];
}

/**
 * Fallback ML stubs have no Wikipedia image or platformIds. A name-matched
 * live Kick/Twitch/YouTube row is still enough to open the person hub.
 */
export function personHubHasSurface(hub, results = []) {
  return hasPersonPortraitPanel(hub)
    || hasLinks(hub)
    || Boolean(pickLiveHeroEvent(results, hub));
}

function liveCover(event) {
  const platform = livePlatform(event);
  const label = LIVE_PLATFORM_LABEL[platform];
  return {
    src: safeMediaUrl(event.thumbnail || event.logo),
    kind: 'live',
    caption: label ? `Live now on ${label}` : 'Live now',
    href: event.url || null,
  };
}

/**
 * Hero cover: live official stream when the gallery has one, else a real venue
 * photo, else album art, else the creator's Twitch/YouTube banner or portrait.
 * Event/tour artwork must not stand in for a stadium shot.
 * Offline streamers still get channel art so the hero is not an empty panel.
 */
export function resolvePersonHeroCover(hub, results = []) {
  const liveEvent = pickLiveHeroEvent(results, hub);
  if (liveEvent) return liveCover(liveEvent);

  const nextEvent = hasNextEvent(hub) ? hub.nextEvent : null;
  const venueSrc = distinctVenueImage(hub, nextEvent);
  const albumSrc = safeMediaUrl(hub?.albumCoverUrl);
  const concertCaption = nextEventLabel(nextEvent);

  if (venueSrc) {
    return {
      src: venueSrc,
      kind: 'venue',
      caption: concertCaption,
      href: null,
    };
  }

  if (albumSrc) {
    return {
      src: albumSrc,
      kind: 'album',
      caption: concertCaption,
      href: null,
    };
  }

  const portraitSrc = safeMediaUrl(hub?.coverImageUrl) || safeMediaUrl(hub?.entity?.imageUrl);
  if (portraitSrc) {
    return {
      src: portraitSrc,
      kind: 'portrait',
      caption: concertCaption,
      href: null,
    };
  }

  return {
    src: null,
    kind: null,
    caption: concertCaption,
    href: null,
  };
}
